import * as React from 'react';

import type {EmailSequenceFragment} from 'nutshell-graphql-types';

import {colors} from '../../colors/colors';
import {Dialog} from '../../dialog/dialog';
import {CancelCircleIcon} from '../../icon/svg/cancel-circle-icon';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {EnvelopeRegularUserIcon} from '../../icon/svg/envelope-regular-user-icon';
import {InfoSquareIcon} from '../../icon/svg/info-square-icon';
import {PlayDoubleIcon} from '../../icon/svg/play-double-icon';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from '../../icon/svg/step-icon';
import {UserOutlineIcon} from '../../icon/svg/user-outline-icon';
import {WarningIcon} from '../../icon/svg/warning-icon';
import {Stack} from '../../layout/stack/stack';
import {StageIndicator} from '../../stage-indicator/stage-indicator';
import {FormattedTimestamp} from '../../timestamp/formatted-timestamp';
import {Body} from '../../typography/body';

import {getTimestampFormat} from '../automated-email-row/helpers';
import {BaseRow} from '../base-row/base-row';

import styles from '../automated-email-row/automated-email-row.module.css';

type Props = {
    title: string | null | undefined;
    emailSequence: EmailSequenceFragment | null | undefined;
    startedFromStageEntered: boolean;
    problemDescription: string | null | undefined;
    startTrigger: string | null | undefined;
    finalEmailSentTime: number | null | undefined;
    onSeeDetails: () => void;
    recipientNames: Array<string | null> | null | undefined;
    onCancel?: () => void;
    onStart?: () => void;
};

export function PersonalEmailSequenceRowSparse(props: Props) {
    const {onSeeDetails, onCancel, onStart} = props;

    const [confirmingCancel, setConfirmingCancel] = React.useState<boolean>(false);
    const [confirmingStart, setConfirmingStart] = React.useState<boolean>(false);
    const cancelButtonRef = React.useRef<HTMLElement | null | undefined>(null);
    const startButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const nextEmailSendTime = React.useMemo(() => {
        let nextEmail =
            props.emailSequence &&
            props.emailSequence.emailsQueued.find((email) => email.isNextScheduledEmail);

        if (!nextEmail) {
            nextEmail =
                props.emailSequence &&
                props.emailSequence.emailsQueued.find((email) => !email.isAlreadySent);
        }

        if (!nextEmail) {
            return null;
        }

        return nextEmail.deliveryTime;
    }, [props.emailSequence]);

    const sendTimeInfo = React.useMemo(() => {
        if (props.finalEmailSentTime) {
            const timestampFormat = getTimestampFormat(props.finalEmailSentTime);

            return (
                <Body size='small'>
                    <Stack spacing={4} isHorizontal={true} alignItems='center' isFullWidth={true}>
                        Final email sent
                        <FormattedTimestamp
                            datetime={Number(props.finalEmailSentTime)}
                            format={timestampFormat}
                        />
                    </Stack>
                </Body>
            );
        } else if (nextEmailSendTime) {
            const timestampFormat = getTimestampFormat(nextEmailSendTime);

            return (
                <Body size='small'>
                    <Stack spacing={4} isHorizontal={true} alignItems='center' isFullWidth={true}>
                        Sending next email
                        <FormattedTimestamp
                            datetime={Number(nextEmailSendTime)}
                            format={timestampFormat}
                        />
                    </Stack>
                </Body>
            );
        } else if (props.startTrigger) {
            return <Body size='small'>{props.startTrigger}</Body>;
        } else {
            return <Body size='small'>No more emails scheduled</Body>;
        }
    }, [nextEmailSendTime, props.finalEmailSentTime, props.startTrigger]);

    const toolbarOptions = React.useMemo(() => {
        const options = [
            {
                id: 'details',
                icon: InfoSquareIcon,
                tooltipText: 'Sequence details',
                onClick: onSeeDetails,
            },
        ];

        if (onStart) {
            options.push({
                id: 'start',
                icon: PlayDoubleIcon,
                tooltipText: 'Start sequence',
                onClick: () => setConfirmingStart(true),
                // @ts-expect-error
                getRef: (ref) => {
                    startButtonRef.current = ref;
                },
            });
        }

        if (onCancel) {
            options.push({
                id: 'cancel',
                icon: CancelCircleIcon,
                tooltipText: 'Cancel sequence',
                onClick: () => setConfirmingCancel(true),
                // @ts-expect-error
                getRef: (ref) => {
                    cancelButtonRef.current = ref;
                },
            });
        }

        return options;
    }, [onSeeDetails, onCancel, onStart]);

    return (
        <>
            <BaseRow
                title={props.title || 'Personal email sequence'}
                type='personal-email-sequence'
                onClick={onSeeDetails}
                customIcon={
                    props.startedFromStageEntered && (
                        <div className='px-6'>
                            <Stack spacing={4} alignItems='center' isFullWidth={true}>
                                {/* @ts-expect-error */}
                                <EnvelopeRegularUserIcon size={20} color={colors.greyLt} />
                                <StepIcon size={12} color={colors.orange} />
                            </Stack>
                        </div>
                    )
                }
                toolbarProps={{
                    options: toolbarOptions,
                    forceShowToolbar: confirmingCancel || confirmingStart,
                }}
            >
                {props.problemDescription ? (
                    <Stack spacing={4} isHorizontal={true} alignItems='center' isFullWidth={true}>
                        {/* @ts-expect-error */}
                        <WarningIcon color={colors.yellow} wrapWithDiv={false} size={14} />
                        <Body size='small'>{props.problemDescription}</Body>
                    </Stack>
                ) : (
                    <Stack spacing={4}>
                        <Stack
                            spacing={4}
                            isHorizontal={true}
                            alignItems='center'
                            isFullWidth={true}
                            wrap={true}
                        >
                            {/* @ts-expect-error */}
                            <ClockRegularIcon size={16} color={colors.grey} wrapWithDiv={false} />
                            {sendTimeInfo}
                            <div className={styles['email-progress-container']}>
                                <StageIndicator
                                    currentStage={
                                        props.emailSequence
                                            ? props.emailSequence.sentEmailsCount
                                            : 0
                                    }
                                    numStages={
                                        props.emailSequence ? props.emailSequence.messageCount : 0
                                    }
                                    customColor={colors.reportGreen}
                                    height={15}
                                />
                            </div>
                        </Stack>
                        {props.recipientNames && (
                            <Stack
                                spacing={4}
                                isHorizontal={true}
                                alignItems='center'
                                isFullWidth={true}
                            >
                                {/* @ts-expect-error */}
                                <UserOutlineIcon size={14} color={colors.grey} />
                                <Body size='small'>
                                    {props.finalEmailSentTime ? 'Sent to' : 'Sending to'}{' '}
                                    {props.recipientNames.join(', ')}
                                </Body>
                            </Stack>
                        )}
                    </Stack>
                )}
            </BaseRow>
            {confirmingCancel && (
                <Dialog
                    anchor={cancelButtonRef.current}
                    question='Are you sure you want to cancel this email sequence?'
                    onPrimary={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        setConfirmingCancel(false);
                    }}
                    onSecondary={() => {
                        setConfirmingCancel(false);
                    }}
                    primaryText='Yes, cancel'
                    secondaryText='No, don’t cancel'
                    isWarning={true}
                    customStyles={{width: '230px'}}
                />
            )}
            {confirmingStart && (
                <Dialog
                    anchor={startButtonRef.current}
                    question='Are you sure you want to start this email sequence?'
                    primaryText='Yes, start'
                    secondaryText='No, don’t start'
                    primaryVariant='primary'
                    secondaryVariant='secondary'
                    onPrimary={() => {
                        if (onStart) {
                            onStart();
                        }
                        setConfirmingStart(false);
                    }}
                    onSecondary={() => {
                        setConfirmingStart(false);
                    }}
                    customStyles={{width: '210px'}}
                />
            )}
        </>
    );
}
